/**
 * Download_Control for the ASK AI CSV download feature.
 *
 * One button, rendered once per detected Answer_Table, that starts the CSV
 * download for that table. `AnswerTableDownloads.tsx` portals one of these
 * into the mount node it inserts after each table, and `useTableCsvDownload.ts`
 * owns the state machine behind `onActivate`.
 *
 * The component is presentational on purpose: it holds no download state,
 * never calls the converter, and never reads the DOM. It receives `busy` and
 * reports activation, which keeps the conversion flow testable without
 * rendering a button and keeps this file's tests about label, accessible name,
 * and progress feedback only.
 */

import React from "react";
import { Button, CircularProgress } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

export interface DownloadControlProps {
  /**
   * Zero-based position index of the Answer_Table this control downloads
   * (Req 1.3). Passed back through `onActivate` so one handler can serve
   * every control of an answer.
   */
  tableIndex: number;
  /** True while this control's table is being converted (Req 6.4). */
  busy: boolean;
  /** Called with `tableIndex` when the control is activated (Req 2.5). */
  onActivate: (tableIndex: number) => void;
}

/**
 * Button that starts the CSV download for one Answer_Table.
 *
 * The visible label is `Download CSV` (Req 2.3) and the accessible name is
 * `Download table as CSV` (Req 2.7), so the label stays short next to the
 * table while a screen reader announces what the control acts on. A native
 * MUI `Button` is focusable and Enter/Space-activatable already, so no extra
 * key handler is needed for keyboard reach; `tabIndex` is deliberately left
 * unset so the control keeps its document-order position in the focus ring.
 *
 * While `busy`, the start icon becomes a `CircularProgress` and `aria-busy`
 * is set, which is the progress feedback a large-table conversion needs
 * (Req 6.4). Outside that window the attribute is absent, which ARIA treats
 * as `aria-busy="false"`.
 *
 * No role check gates rendering (Req 2.8): the effective role, the team
 * context, and the permission helpers are deliberately not consulted here,
 * because a user who can read the rendered table can already read the data.
 */
const DownloadControl: React.FC<DownloadControlProps> = ({
  tableIndex,
  busy,
  onActivate,
}) => (
  <Button
    size="small"
    aria-label="Download table as CSV"
    aria-busy={busy ? true : undefined}
    startIcon={busy ? <CircularProgress size={16} /> : <FileDownloadIcon />}
    onClick={() => onActivate(tableIndex)}
  >
    Download CSV
  </Button>
);

export default DownloadControl;
